import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  contactTaskAPI,
  contactLogAPI,
  mondayDigestAPI,
  ContactTask,
  MondayDigestPreview,
  RelationshipUpdate,
} from '../../services/crmAPI';
import CRMSubNav from './CRMSubNav';
import './CRM.css';

interface CompleteTaskModalProps {
  task: ContactTask;
  onClose: () => void;
  onComplete: (taskId: number, data: {
    notes: string;
    contact_type: string;
    follow_up_needed: boolean;
    follow_up_date?: string;
    follow_up_notes?: string;
  }) => void;
}

/**
 * Dismissing closes a task WITHOUT recording a contact.
 *
 * Completing writes a ContactLog, and ContactLog.save() sets Customer.last_contacted_at - so
 * clearing a backlog by completing it marks those customers freshly contacted and empties the
 * 30-Day No Contact list. That made the backlog unworkable: the only way to clear it was to
 * corrupt the outreach list it feeds.
 *
 * Deliberately plainer than the complete modal. Dismissing is the exception, and the copy
 * states what it does and does not record so the choice is never accidental.
 */
const DismissTaskModal: React.FC<{
  task: ContactTask;
  onClose: () => void;
  onDismiss: (taskId: number, reason: string, note: string) => Promise<void>;
}> = ({ task, onClose, onDismiss }) => {
  const [reasons, setReasons] = useState<{ value: string; label: string }[]>([]);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Fetched, not hardcoded - the list cannot drift from the model's choices.
    contactTaskAPI.dismissalReasons()
      .then(r => setReasons(r.data))
      .catch(() => setReasons([]));
  }, []);

  const needsNote = reason === 'OTHER';
  const canSubmit = !!reason && (!needsNote || !!note.trim()) && !submitting;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onDismiss(task.id, reason, note.trim());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <h3 style={{ color: '#92400e' }}>Dismiss Task</h3>
        <p style={{ color: '#555', fontSize: 14, marginTop: 4 }}>
          <strong>{task.customer_name}</strong> — {task.title}
        </p>
        <div style={{
          background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6,
          padding: '10px 12px', margin: '12px 0', fontSize: 13, lineHeight: 1.5,
        }}>
          This closes the task <strong>without</strong> recording a contact. No contact log is
          written and the customer's last-contacted date is unchanged, so they stay on the
          30-day outreach list if they belong there.
        </div>

        <form onSubmit={submit}>
          <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
            Why is this being dismissed?
          </label>
          <select
            value={reason}
            onChange={e => setReason(e.target.value)}
            style={{ width: '100%', minHeight: 40, padding: '8px', marginBottom: 12 }}
          >
            <option value="">Choose a reason…</option>
            {reasons.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>

          <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
            Note {needsNote ? '(required)' : '(optional)'}
          </label>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={3}
            style={{ width: '100%', padding: '8px', marginBottom: 12, boxSizing: 'border-box' }}
          />

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-outline" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn"
              disabled={!canSubmit}
              style={{
                background: canSubmit ? '#92400e' : '#ccc', color: '#fff', border: 'none',
              }}
            >
              {submitting ? 'Dismissing…' : 'Dismiss Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CompleteTaskModal: React.FC<CompleteTaskModalProps> = ({ task, onClose, onComplete }) => {
  const [notes, setNotes] = useState('');
  const [contactType, setContactType] = useState('PHONE');
  const [followUpNeeded, setFollowUpNeeded] = useState(false);
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpNotes, setFollowUpNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showRelationship, setShowRelationship] = useState(false);
  const [relSpouse, setRelSpouse] = useState(task.spouse_name || '');
  const [relChildren, setRelChildren] = useState(task.children_info || '');
  const [relPets, setRelPets] = useState(task.pets_info || '');
  const [relHobbies, setRelHobbies] = useState(task.hobbies_interests || '');
  const [relTalkingPoints, setRelTalkingPoints] = useState(task.talking_points || '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!notes.trim()) {
      alert('Please enter contact notes');
      return;
    }
    setSubmitting(true);
    await onComplete(task.id, {
      notes,
      contact_type: contactType,
      follow_up_needed: followUpNeeded,
      follow_up_date: followUpNeeded ? followUpDate : undefined,
      follow_up_notes: followUpNeeded ? followUpNotes : undefined,
    });

    // Non-blocking relationship update
    if (showRelationship) {
      const relData: RelationshipUpdate = {};
      if (relSpouse !== (task.spouse_name || '')) relData.spouse_name = relSpouse;
      if (relChildren !== (task.children_info || '')) relData.children_info = relChildren;
      if (relPets !== (task.pets_info || '')) relData.pets_info = relPets;
      if (relHobbies !== (task.hobbies_interests || '')) relData.hobbies_interests = relHobbies;
      if (relTalkingPoints !== (task.talking_points || '')) relData.talking_points = relTalkingPoints;
      if (Object.keys(relData).length > 0) {
        contactTaskAPI.updateCustomerRelationship(task.customer_id, relData).catch(err => {
          console.error('Failed to update relationship data:', err);
        });
      }
    }

    setSubmitting(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content complete-task-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Complete Task</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          <div className="task-info-summary">
            <strong>{task.customer_name}</strong>
            <p>{task.title}</p>
            {task.customer_phone && <p>Phone: {task.customer_phone}</p>}
            {task.preferred_contact_method && (
              <p>Preferred: {task.preferred_contact_method}{task.best_time_to_contact ? ` (${task.best_time_to_contact})` : ''}</p>
            )}
            {task.talking_points && (
              <p className="summary-talking-points">"{task.talking_points}"</p>
            )}
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Contact Type</label>
              <select value={contactType} onChange={e => setContactType(e.target.value)}>
                <option value="PHONE">Phone Call</option>
                <option value="TEXT">Text Message</option>
                <option value="EMAIL">Email</option>
                <option value="IN_PERSON">In Person</option>
                <option value="VOICEMAIL">Voicemail</option>
              </select>
            </div>

            <div className="form-group">
              <label>Contact Notes *</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="What was discussed? Any important details?"
                rows={4}
                required
              />
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={followUpNeeded}
                  onChange={e => setFollowUpNeeded(e.target.checked)}
                />
                Schedule Follow-up
              </label>
            </div>

            {followUpNeeded && (
              <>
                <div className="form-group">
                  <label>Follow-up Date</label>
                  <input
                    type="date"
                    value={followUpDate}
                    onChange={e => setFollowUpDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="form-group">
                  <label>Follow-up Notes</label>
                  <textarea
                    value={followUpNotes}
                    onChange={e => setFollowUpNotes(e.target.value)}
                    placeholder="What should be followed up on?"
                    rows={2}
                  />
                </div>
              </>
            )}

            <div className="relationship-update-section">
              <button
                type="button"
                className="relationship-toggle"
                onClick={() => setShowRelationship(!showRelationship)}
              >
                {showRelationship ? '− Hide Relationship Notes' : '+ Update Relationship Notes'}
              </button>
              {showRelationship && (
                <div className="relationship-fields">
                  <div className="form-row-compact">
                    <div className="form-group">
                      <label>Spouse</label>
                      <input type="text" value={relSpouse} onChange={e => setRelSpouse(e.target.value)} placeholder="Spouse name" />
                    </div>
                    <div className="form-group">
                      <label>Kids</label>
                      <input type="text" value={relChildren} onChange={e => setRelChildren(e.target.value)} placeholder="Children info" />
                    </div>
                  </div>
                  <div className="form-row-compact">
                    <div className="form-group">
                      <label>Pets</label>
                      <input type="text" value={relPets} onChange={e => setRelPets(e.target.value)} placeholder="Pets info" />
                    </div>
                    <div className="form-group">
                      <label>Hobbies / Interests</label>
                      <input type="text" value={relHobbies} onChange={e => setRelHobbies(e.target.value)} placeholder="Hobbies & interests" />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Talking Points</label>
                    <textarea value={relTalkingPoints} onChange={e => setRelTalkingPoints(e.target.value)} placeholder="Key talking points for next call" rows={2} />
                  </div>
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Saving...' : 'Complete Task'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const MondayTaskList: React.FC = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<ContactTask[]>([]);
  const [digest, setDigest] = useState<MondayDigestPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState<ContactTask | null>(null);
  const [dismissingTask, setDismissingTask] = useState<ContactTask | null>(null);
  const [expandedTask, setExpandedTask] = useState<number | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [tasksRes, digestRes] = await Promise.all([
        contactTaskAPI.getMyMonday(),
        mondayDigestAPI.getPreview(),
      ]);
      setTasks(tasksRes.data.tasks);
      setDigest(digestRes.data);
    } catch (err) {
      console.error('Error loading Monday tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDismissTask = async (taskId: number, reason: string, note: string) => {
    try {
      await contactTaskAPI.dismiss(taskId, { reason, note });
      setTasks(prev => prev.filter(t => t.id !== taskId));
      setDismissingTask(null);
    } catch (err: any) {
      console.error('Error dismissing task:', err);
      alert(err?.response?.data?.error || 'Failed to dismiss task');
    }
  };

  const handleCompleteTask = async (taskId: number, data: {
    notes: string;
    contact_type: string;
    follow_up_needed: boolean;
    follow_up_date?: string;
    follow_up_notes?: string;
  }) => {
    try {
      await contactTaskAPI.complete(taskId, data);
      setTasks(prev => prev.filter(t => t.id !== taskId));
      setSelectedTask(null);
    } catch (err) {
      console.error('Error completing task:', err);
      alert('Failed to complete task');
    }
  };

  const getTaskTypeColor = (type: string) => {
    switch (type) {
      case 'POST_DELIVERY': return '#28a745';
      case 'NO_CONTACT_30': return '#ffc107';
      case 'NO_CONTACT_45': return '#fd7e14';
      case 'NO_CONTACT_60': return '#dc3545';
      case 'INTRODUCTION': return '#17a2b8';
      case 'FOLLOW_UP': return '#6c757d';
      default: return '#6c757d';
    }
  };

  const getPriorityLabel = (priority: number) => {
    switch (priority) {
      case 1: return { label: 'Urgent', color: '#dc3545' };
      case 2: return { label: 'High', color: '#fd7e14' };
      case 3: return { label: 'Medium', color: '#ffc107' };
      case 4: return { label: 'Low', color: '#28a745' };
      default: return { label: 'Normal', color: '#6c757d' };
    }
  };

  const generateMailtoLink = (task: ContactTask) => {
    const subject = encodeURIComponent(`Following up - Red Bridge Farm Feed Co.`);
    const body = encodeURIComponent(
      `Hi ${task.customer_name?.split(' ')[0] || 'there'},\n\n` +
      `I hope this email finds you well! I wanted to reach out and check in with you.\n\n` +
      `${task.talking_points ? `Note: ${task.talking_points}\n\n` : ''}` +
      `Please let me know if there's anything I can help you with.\n\n` +
      `Best regards,\n` +
      `Red Bridge Farm Feed Co.`
    );
    return `mailto:${task.customer_email || ''}?subject=${subject}&body=${body}`;
  };

  if (loading) {
    return (
      <div className="crm-page">
        <CRMSubNav />
        <div className="loading">Loading Monday tasks...</div>
      </div>
    );
  }

  return (
    <div className="crm-page monday-task-page">
      <CRMSubNav />
      {/* Header with Verse */}
      <div className="monday-header">
        <div className="monday-title-section">
          <h1>Monday Morning Connections</h1>
          <p className="week-label">{digest?.week_of}</p>
        </div>

        {digest?.verse && (
          <div className="verse-card">
            <p className="verse-text">"{digest.verse.text}"</p>
            <p className="verse-reference">{digest.verse.reference}</p>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="monday-stats">
        <div className="stat-card">
          <div className="stat-value">{tasks.length}</div>
          <div className="stat-label">Tasks This Week</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{tasks.filter(t => t.is_overdue).length}</div>
          <div className="stat-label">Overdue</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{digest?.birthdays?.length || 0}</div>
          <div className="stat-label">Birthdays</div>
        </div>
      </div>

      {/* Upcoming Birthdays */}
      {digest?.birthdays && digest.birthdays.length > 0 && (
        <div className="birthdays-section">
          <h3>Upcoming Birthdays</h3>
          <div className="birthday-cards">
            {digest.birthdays.map((bday, idx) => (
              <div key={idx} className="birthday-card">
                <span className="birthday-icon">🎂</span>
                <div className="birthday-info">
                  <strong>{bday.name}</strong>
                  <span>{bday.date}</span>
                  {bday.is_spouse && <small>(Spouse of {bday.customer_name})</small>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Task List */}
      <div className="monday-tasks">
        <div className="section-header">
          <h2>Connection Tasks</h2>
          <button className="btn btn-outline btn-sm" onClick={() => navigate('/crm/no-contact')}>
            View 30-Day List
          </button>
        </div>

        {tasks.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🎉</div>
            <h3>All caught up!</h3>
            <p>You have no contact tasks this week.</p>
          </div>
        ) : (
          <div className="task-list-container">
            {tasks.map(task => {
              const priority = getPriorityLabel(task.priority);
              const isExpanded = expandedTask === task.id;

              return (
                <div
                  key={task.id}
                  className={`contact-task-card ${task.is_overdue ? 'overdue' : ''}`}
                  style={{ borderLeftColor: getTaskTypeColor(task.task_type) }}
                >
                  <div className="task-main" onClick={() => setExpandedTask(isExpanded ? null : task.id)}>
                    <div className="task-header-row">
                      <div className="task-title-section">
                        <span
                          className="task-type-badge"
                          style={{ backgroundColor: getTaskTypeColor(task.task_type) }}
                        >
                          {task.task_type_display}
                        </span>
                        <h4>{task.customer_name}</h4>
                      </div>
                      <div className="task-meta-section">
                        <span
                          className="priority-badge"
                          style={{ backgroundColor: priority.color }}
                        >
                          {priority.label}
                        </span>
                        <span className={`due-date ${task.is_overdue ? 'overdue' : ''}`}>
                          {task.is_overdue ? 'Overdue' : `Due: ${new Date(task.due_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`}
                        </span>
                      </div>
                    </div>

                    <p className="task-title">{task.title}</p>

                    <div className="task-contact-row">
                      {task.customer_phone && (
                        <a href={`tel:${task.customer_phone}`} className="contact-link phone" onClick={e => e.stopPropagation()}>
                          📞 {task.customer_phone}
                        </a>
                      )}
                      {task.customer_email && (
                        <a href={generateMailtoLink(task)} className="contact-link email" onClick={e => e.stopPropagation()}>
                          ✉️ Email
                        </a>
                      )}
                      {task.customer_city && (
                        <span className="location">
                          📍 {task.customer_city}, {task.customer_state}
                        </span>
                      )}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="task-expanded">
                      {task.description && (
                        <div className="expanded-section">
                          <label>Notes:</label>
                          <p>{task.description}</p>
                        </div>
                      )}

                      {task.talking_points && (
                        <div className="expanded-section talking-points">
                          <label>Talking Points:</label>
                          <p>{task.talking_points}</p>
                        </div>
                      )}

                      {(task.spouse_name || task.birthday || task.children_info || task.pets_info || task.hobbies_interests) && (
                        <div className="relationship-intel">
                          <label>Relationship Intel:</label>
                          <div className="relationship-items">
                            {task.spouse_name && <span className="rel-item">Spouse: {task.spouse_name}</span>}
                            {task.birthday && <span className="rel-item">Birthday: {new Date(task.birthday + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                            {task.children_info && <span className="rel-item">Kids: {task.children_info}</span>}
                            {task.pets_info && <span className="rel-item">Pets: {task.pets_info}</span>}
                            {task.hobbies_interests && <span className="rel-item">Interests: {task.hobbies_interests}</span>}
                          </div>
                        </div>
                      )}

                      {(task.preferred_contact_method || task.best_time_to_contact) && (
                        <div className="contact-prefs">
                          {task.preferred_contact_method && <span>Preferred: {task.preferred_contact_method}</span>}
                          {task.best_time_to_contact && <span>Best time: {task.best_time_to_contact}</span>}
                        </div>
                      )}

                      {task.days_since_contact !== null && task.days_since_contact !== undefined && (
                        <div className="expanded-section">
                          <label>Last Contact:</label>
                          <p>
                            {task.last_contacted_at
                              ? `${task.days_since_contact} days ago (${new Date(task.last_contacted_at).toLocaleDateString()})`
                              : 'Never contacted'}
                          </p>
                        </div>
                      )}

                      {task.related_order_number && (
                        <div className="expanded-section">
                          <label>Related Order:</label>
                          <p>#{task.related_order_number}</p>
                        </div>
                      )}

                      <div className="task-actions">
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => navigate(`/crm/customers/${task.customer_id}`)}
                        >
                          View Customer
                        </button>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => setSelectedTask(task)}
                        >
                          Mark Complete
                        </button>
                        {/* Visually subordinate to Complete on purpose: dismissing is the
                            exception, and it records that the work was NOT done. */}
                        <button
                          className="btn btn-sm btn-outline"
                          style={{ color: '#92400e', borderColor: '#fcd34d' }}
                          onClick={() => setDismissingTask(task)}
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Dismiss Task Modal */}
      {dismissingTask && (
        <DismissTaskModal
          task={dismissingTask}
          onClose={() => setDismissingTask(null)}
          onDismiss={handleDismissTask}
        />
      )}

      {/* Complete Task Modal */}
      {selectedTask && (
        <CompleteTaskModal
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onComplete={handleCompleteTask}
        />
      )}
    </div>
  );
};

export default MondayTaskList;
